/**
 * LSP-19608: a conversation that was just created client-side can, for a short window, still 404
 * on reads that depend on it (sparks, media) because the backend hasn't finished propagating it.
 * This wraps such reads in a short, bounded retry that tolerates that transient 404 (and network
 * blips), without turning into a fail-open retry for genuine, permanent errors.
 */
import { retryWithBackoff, type RetryOptions } from "@common-ui/utilities/retry";

/** Bounded window for "row was just created and is not readable yet": 500 + 1000 + 2000 ms (+ jitter). */
export const FRESH_ENTITY_RETRY_OPTIONS = { retries: 3, baseDelayMs: 500 } as const;

type StatusLike = { httpStatus?: unknown; status?: unknown; code?: unknown; message?: unknown };

/** Extracts a numeric HTTP status, preferring `httpStatus` (api._handleResponse's shape) over a possibly-string `code`. */
export function extractHttpStatus(error: unknown): number | undefined {
	if (!error || typeof error !== "object") return undefined;
	const candidate = error as StatusLike;

	for (const value of [candidate.httpStatus, candidate.status, candidate.code]) {
		const numeric = Number(value);
		if (typeof value !== "object" && Number.isFinite(numeric) && numeric >= 100 && numeric <= 599) {
			return numeric;
		}
	}

	if (typeof candidate.message === "string") {
		const match = candidate.message.match(/\bHTTP\s+(\d{3})\b/i);
		if (match) return Number(match[1]);
	}

	return undefined;
}

const NETWORK_ERROR_PATTERN = /failed to fetch|load failed|networkerror|did not go through|appear to be offline/i;

const isAbort = (error: unknown): boolean => {
	if (!error || typeof error !== "object") return false;
	const candidate = error as { name?: unknown; code?: unknown };
	return candidate.name === "AbortError" || candidate.code === "ABORT_ERR";
};

/**
 * Retry predicate for a "read something that might not exist yet" call: retries a 404, 429, or 5xx,
 * and network failures (including `_fetch`'s rewritten "did not go through"/"appear to be offline"
 * messages). Any other 4xx, an aborted request, or an error with no recognizable status/network
 * signature is treated as permanent — deliberately NOT fail-open like `retry.ts`'s default, since an
 * unrecognized error here is more likely a programming error in the response mapping than a transient
 * read.
 */
export function isFreshEntityReadRetryable(error: unknown): boolean {
	if (isAbort(error)) return false;

	const status = extractHttpStatus(error);
	if (status !== undefined) {
		return status === 404 || status === 429 || status >= 500;
	}

	const message = error instanceof Error ? error.message : typeof error === "string" ? error : undefined;
	return typeof message === "string" && NETWORK_ERROR_PATTERN.test(message);
}

export type FreshEntityReadOptions<T> = {
	/** Return true to stop retrying (request superseded / component unmounted / entity switched). */
	isSuperseded?: () => boolean;
	/** When false, a 404 is treated as permanent (e.g. a project-derived reader on a cross-org row). Default true. */
	tolerate404?: boolean;
	/** Test injection passthrough. */
	sleep?: RetryOptions["sleep"];
	retries?: number;
	baseDelayMs?: number;
};

const SUPERSEDED = Symbol("readFreshEntity:superseded");

export function readFreshEntity<T>(operation: () => Promise<T>, options: FreshEntityReadOptions<T> = {}): Promise<T> {
	// The very first attempt always runs. From the second attempt on (i.e. after we've already slept
	// once waiting to retry), re-check isSuperseded before firing the network call again — a caller
	// (e.g. the entity the request was for changed) may have gone stale while we were asleep, and there
	// is no reason to spend another request on data nobody wants any more.
	let attempted = false;
	let lastRealError: unknown;

	const guardedOperation = (): Promise<T> => {
		if (attempted && options.isSuperseded?.()) {
			return Promise.reject(SUPERSEDED);
		}
		attempted = true;
		return operation();
	};

	return retryWithBackoff(guardedOperation, {
		retries: options.retries ?? FRESH_ENTITY_RETRY_OPTIONS.retries,
		baseDelayMs: options.baseDelayMs ?? FRESH_ENTITY_RETRY_OPTIONS.baseDelayMs,
		sleep: options.sleep,
		isRetryable: (error) => {
			if (error === SUPERSEDED) return false;
			lastRealError = error;
			if (options.isSuperseded?.()) return false;
			if (extractHttpStatus(error) === 404 && options.tolerate404 === false) return false;
			return isFreshEntityReadRetryable(error);
		}
	}).catch((error) => {
		throw error === SUPERSEDED ? lastRealError : error;
	});
}
